// Encart « Bon retour » (demande du propriétaire) : visible UNIQUEMENT pour un
// visiteur RÉCURRENT quand une nouveauté (article du blogue OU produit ajouté à
// la boutique) est parue depuis sa dernière visite. Refermable (« Ne plus
// afficher » mémorise la nouveauté — l'encart ne se réaffiche que pour la
// PROCHAINE). La date de visite est conservée en localStorage (mt-last-visit) ;
// jamais affiché à la toute première visite.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, X } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../i18n/LanguageContext";

const LS_VISIT = "mt-last-visit";
const LS_SEEN = "mt-seen-novelty";

export function ReturningVisitor() {
  const { t, ts, lang } = useLang();
  const [novelty, setNovelty] = useState(null);  // { post, products }

  useEffect(() => {
    let lastVisit = 0;
    try {
      lastVisit = Number(localStorage.getItem(LS_VISIT) || 0);
      localStorage.setItem(LS_VISIT, String(Date.now()));
    } catch { return; }  // navigation privée stricte : pas d'encart
    if (!lastVisit) return;  // première visite : rien à comparer
    const seen = localStorage.getItem(LS_SEEN) || "";
    Promise.all([
      api.get("/posts").then(({ data }) => (Array.isArray(data) ? data : (data.posts || []))).catch(() => []),
      api.get("/products", { params: { sort: "new", limit: 8 } }).then(({ data }) => (Array.isArray(data) ? data : [])).catch(() => []),
    ]).then(([posts, products]) => {
      const newestPost = [...posts]
        .sort((a, b) => String(b.created_at).localeCompare(String(a.created_at)))[0];
      const post = newestPost && Date.parse(newestPost.created_at) > lastVisit ? newestPost : null;
      const newProducts = products.filter((p) => Date.parse(p.created_at || "") > lastVisit);
      if (!post && newProducts.length === 0) return;
      // Signature de la nouveauté : ne se réaffiche que si elle change
      const signature = `${post ? post.id : ""}|${newProducts.map((p) => p.created_at).join(",")}`;
      if (signature === seen) return;
      setNovelty({ post, products: newProducts, signature });
    });
  }, []);

  if (!novelty) return null;
  const { post, products, signature } = novelty;
  const colon = lang === "fr" ? " : " : ": ";
  const postTitle = post ? (lang === "en" && post.title_en ? post.title_en : post.title) : null;
  const productNames = products.slice(0, 3).map((p) =>
    lang === "en" && p.name_en ? p.name_en : (p.name_fr || p.name));
  const moreCount = products.length - productNames.length;
  const dismiss = () => {
    try { localStorage.setItem(LS_SEEN, signature); } catch { /* silencieux */ }
    setNovelty(null);
  };

  return (
    <section className="relative z-10 px-5 sm:px-6 mt-8 mb-2" data-testid="returning-visitor">
      <div className="max-w-3xl mx-auto bg-surface/90 backdrop-blur border border-gold/30 rounded-2xl p-5 sm:p-6 shadow-[0_8px_40px_rgba(212,175,55,0.12)] flex items-start gap-4">
        <Sparkles className="w-6 h-6 text-gold shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] uppercase tracking-widest text-gold mb-1">{t("home.back_kicker")}</p>
          {post && (
            <p className="text-sm text-ivory/85 mb-1" data-testid="returning-visitor-post">
              {t("home.back_new")}{colon}<strong className="text-champagne">{postTitle}</strong>
            </p>
          )}
          {products.length > 0 && (
            <p className="text-sm text-ivory/85 mb-3" data-testid="returning-visitor-products">
              {products.length} {t("home.back_products")}{colon}
              <strong className="text-champagne">
                {productNames.join(" · ")}{moreCount > 0 ? ` +${moreCount}` : ""}
              </strong>
            </p>
          )}
          <div className="flex flex-wrap items-center gap-3">
            {post && (
              <Link to={`/blogue/${post.slug}`} data-testid="returning-visitor-read"
                className="inline-flex items-center gap-2 bg-gold text-ink text-xs font-bold px-5 py-2 rounded-full hover:bg-champagne transition-colors duration-300">
                {t("home.back_read")}
              </Link>
            )}
            <Link to="/boutique" data-testid="returning-visitor-shop"
              className={`text-xs underline underline-offset-4 transition-colors duration-300 ${post ? "text-ivory/60 hover:text-gold" : "inline-flex items-center gap-2 bg-gold text-ink font-bold px-5 py-2 rounded-full hover:bg-champagne no-underline"}`}>
              {t("home.back_shop")}
            </Link>
            <button onClick={dismiss} data-testid="returning-visitor-hide"
              className="text-[11px] text-ivory/40 hover:text-ivory/70 transition-colors duration-300 ml-auto">
              {t("home.back_hide")}
            </button>
          </div>
        </div>
        <button onClick={dismiss} data-testid="returning-visitor-close" aria-label={ts("home.back_hide")}
          className="text-ivory/40 hover:text-gold transition-colors duration-300 shrink-0">
          <X className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}
